import { BellRing, CreditCard, Timer, TrendingUp } from "lucide-react";
import type { ReactNode } from "react";

import {
  BookingScreen,
  ProgressScreen,
  ReminderScreen,
  WorkoutScreen,
  kilos,
} from "@/features/portal/components/app-screens";
import { CoachDesk } from "@/features/portal/components/coach-desk";
import { CoachFinder } from "@/features/portal/components/coach-finder";
import { Laptop, Phone } from "@/features/portal/components/devices";
import { Accent, displayTitle, Eyebrow, GoLink, Section } from "@/features/portal/components/portal-ui";
import { FloatChip, Stage } from "@/features/portal/components/stage";
import { cn } from "@/lib/utils";
import { m } from "@/paraglide/messages.js";

/** A phone standing in a wide tile, whole, with up to two chips floating beside it. */
const StandingPhone = ({ children, chips }: { children: ReactNode; chips?: ReactNode }) => (
  <div className="relative w-[min(290px,78%)] lg:w-[310px]">
    <Phone>{children}</Phone>
    {chips}
  </div>
);

const STEPS = [
  {
    id: "find",
    short: () => m.how_page_rail_find(),
    title: () => m.how_page_find_title(),
    lead: () => m.how_page_find_lead(),
    body: () => m.how_page_find_body(),
    art: "relative min-h-[460px] lg:min-h-0",
    picture: () => <CoachFinder />,
  },
  {
    id: "book",
    short: () => m.how_page_rail_book(),
    title: () => m.how_page_book_title(),
    lead: () => m.how_page_book_lead(),
    body: () => m.how_page_book_body(),
    art: "flex justify-center px-6 pb-10 lg:items-center lg:py-14",
    picture: () => (
      <StandingPhone
        chips={
          <FloatChip className="top-[70%] -left-[30%] max-lg:hidden">
            <CreditCard className="size-4 text-primary" />
            {m.how_page_book_chip()}
          </FloatChip>
        }
      >
        <BookingScreen />
      </StandingPhone>
    ),
  },
  {
    id: "remind",
    short: () => m.how_page_rail_remind(),
    title: () => m.how_page_remind_title(),
    lead: () => m.how_page_remind_lead(),
    body: () => m.how_page_remind_body(),
    art: "flex justify-center px-6 pb-10 lg:items-center lg:py-14",
    picture: () => (
      <StandingPhone
        chips={
          <FloatChip className="top-[26%] -right-[30%] max-lg:hidden">
            <BellRing className="size-4 text-primary" />
            {m.how_page_remind_chip()}
          </FloatChip>
        }
      >
        <ReminderScreen />
      </StandingPhone>
    ),
  },
  {
    id: "train",
    short: () => m.how_page_rail_train(),
    title: () => m.how_page_train_title(),
    lead: () => m.how_page_train_lead(),
    body: () => m.how_page_train_body(),
    art: "flex justify-center px-6 pb-10 lg:items-center lg:py-14",
    picture: () => (
      <StandingPhone
        chips={
          <>
            <FloatChip className="top-[30%] -left-[36%] max-lg:hidden">
              <Timer className="size-4 text-primary" />
              {m.features_train_chip_rest()}
            </FloatChip>
            <FloatChip className="top-[60%] -right-[32%] max-lg:hidden">
              <TrendingUp className="size-4 text-primary" />
              {m.features_train_chip_last({ kg: kilos(2.5) })}
            </FloatChip>
          </>
        }
      >
        <WorkoutScreen />
      </StandingPhone>
    ),
  },
  {
    id: "grow",
    short: () => m.how_page_rail_grow(),
    title: () => m.how_page_grow_title(),
    lead: () => m.how_page_grow_lead(),
    body: () => m.how_page_grow_body(),
    art: "flex items-center justify-center px-5 pt-2 pb-12 lg:py-16 lg:pr-12 lg:pl-0",
    picture: () => (
      <div className="relative w-full max-w-[800px] pr-[10%]">
        <Laptop>
          <CoachDesk />
        </Laptop>
        <Phone className="absolute right-0 -bottom-[7%] w-[20%]">
          <ProgressScreen />
        </Phone>
      </div>
    ),
  },
];

const stepNumber = (index: number) => String(index + 1).padStart(2, "0");

/**
 * The words that open `/how-it-works`, inside the head card, and the whole way in as a rail of
 * numbered stops, each a jump to its own tile below.
 */
export const HowHero = () => (
  <div className="relative mx-auto max-w-[920px] px-1 pt-14 pb-2 text-center md:pt-20">
    <Eyebrow>{m.portal_nav_how()}</Eyebrow>
    <h1 className={cn(displayTitle, "mt-6")}>
      {m.how_page_title_lead()} <Accent>{m.how_page_title_tail()}</Accent>
    </h1>
    <p className="mx-auto mt-6 max-w-[54ch] text-lg leading-[1.55] text-pretty text-muted-foreground">
      {m.how_page_lede()}
    </p>
    <div className="mt-8 flex flex-wrap justify-center gap-3">
      <GoLink to="/coaches" tone="lime">
        {m.portal_features_closing_cta()}
      </GoLink>
    </div>
    <ol className="mx-auto mt-14 flex max-w-[760px] flex-wrap justify-center gap-2 md:mt-16">
      {STEPS.map((step, index) => (
        <li key={step.id}>
          <a
            href={`#step-${step.id}`}
            className="group inline-flex h-11 items-center gap-2.5 rounded-full border border-border bg-background/70 pr-4 pl-1.5 text-sm font-medium backdrop-blur-xl transition-colors hover:bg-muted"
          >
            <span className="grid size-8 place-items-center rounded-full bg-primary text-[12px] font-semibold text-primary-foreground tabular-nums transition-transform duration-200 group-hover:scale-105">
              {stepNumber(index)}
            </span>
            {step.short()}
          </a>
        </li>
      ))}
    </ol>
  </div>
);

/** Each stop of the way, one wide tile apiece, the pictures zigzagging left and right. */
export const HowSteps = () => (
  <Section>
    <div className="grid gap-3">
      {STEPS.map((step, index) => (
        <Stage
          key={step.id}
          id={`step-${step.id}`}
          side
          flip={index % 2 === 1}
          eyebrow={m.how_page_step({ number: stepNumber(index) })}
          title={step.title()}
          lead={step.lead()}
          body={step.body()}
          className="lg:min-h-[640px]"
          artClassName={step.art}
        >
          {step.picture()}
        </Stage>
      ))}
    </div>
  </Section>
);
